// ═══════════════════════════════════════════════════════════════════════════
// DONATAPP VOICE v9 - Monocromatico, NO emoji, Log leggibili
// ═══════════════════════════════════════════════════════════════════════════

import React, { useState, useRef, useEffect, useCallback } from 'react';

// CONFIG
const API_BASE_URL = 'https://api.semanticai.it';
const API_KEY = 'DonatApp2025!';

// PALETTE MONOCROMATICA + rosso solo cuore
const C = {
  heart: '#DC2626',
  heartLight: '#FCA5A5',
  black: '#000000',
  gray900: '#111111',
  gray800: '#222222',
  gray700: '#333333',
  gray600: '#555555',
  gray500: '#777777',
  gray400: '#999999',
  gray300: '#BBBBBB',
  gray200: '#DDDDDD',
  gray100: '#EEEEEE',
  gray50: '#F5F5F5',
  white: '#FFFFFF',
};

// VOCI
const VOCI_11 = [
  { id: 'bella', nome: 'Caterina' },
  { id: 'dottore', nome: 'Dottore' },
  { id: 'renzo', nome: 'Renzo' },
];
const VOCI_OAI = [
  { id: 'nova', nome: 'Nova' },
  { id: 'alloy', nome: 'Alloy' },
  { id: 'echo', nome: 'Echo' },
  { id: 'shimmer', nome: 'Shimmer' },
];

// FRASI
const FRASI = [
  'Che ore sono?',
  'Che giorno è oggi?',
  'Ho fame',
  'Devo prendere le medicine?',
  'Dov\'è Renzo?',
  'Dov\'è Maria?',
  'Mi sento triste',
  'Raccontami qualcosa',
];

// ─────────────────────────────────────────────────────────────────────────────
// COMPONENTE
// ─────────────────────────────────────────────────────────────────────────────
const VoiceECuore = () => {
  const [stato, setStato] = useState('idle');
  const [utente, setUtente] = useState('');
  const [risposta, setRisposta] = useState('');
  const [errore, setErrore] = useState('');
  const [inputText, setInputText] = useState(''); // Input testuale
  
  // 3 LOG SEPARATI
  const [logRag, setLogRag] = useState([]);
  const [logSemantic, setLogSemantic] = useState([]);
  const [logSystem, setLogSystem] = useState([]);
  
  // Visibilità log
  const [showRagLog, setShowRagLog] = useState(true);
  const [showSemanticLog, setShowSemanticLog] = useState(true);
  const [showSystemLog, setShowSystemLog] = useState(false);
  
  // Flusso dati
  const [dataFlow, setDataFlow] = useState(null);
  
  // TTS
  const [servizio, setServizio] = useState('elevenlabs');
  const [voce11, setVoce11] = useState('bella');
  const [voceOAI, setVoceOAI] = useState('nova');
  
  // Mic
  const [mics, setMics] = useState([]);
  const [micId, setMicId] = useState('');
  const [livello, setLivello] = useState(0);
  const [monitoring, setMonitoring] = useState(false);
  const [micOk, setMicOk] = useState(false); // Microfono testato OK
  
  // Conn
  const [conn, setConn] = useState({ stt: null, chat: null, tts11: null, ttsOAI: null });
  
  // Tempi
  const [tempi, setTempi] = useState({});
  
  // UI
  const [isMobile, setIsMobile] = useState(false);
  
  // Refs
  const recorderRef = useRef(null);
  const chunksRef = useRef([]);
  const streamRef = useRef(null);
  const animRef = useRef(null);
  const ctxRef = useRef(null);

  useEffect(() => {
    const check = () => setIsMobile(window.innerWidth < 600);
    check();
    window.addEventListener('resize', check);
    return () => window.removeEventListener('resize', check);
  }, []);

  // ─────────────────────────────────────────────────────────────────────────
  // LOG
  // ─────────────────────────────────────────────────────────────────────────
  const ts = () => new Date().toLocaleTimeString('it-IT', { hour: '2-digit', minute: '2-digit', second: '2-digit' });
  
  const addRagLog = useCallback((msg, type = 'info') => {
    setLogRag(p => [...p.slice(-50), { ts: ts(), msg, type }]);
  }, []);
  
  const addSemanticLog = useCallback((msg, type = 'info') => {
    setLogSemantic(p => [...p.slice(-50), { ts: ts(), msg, type }]);
  }, []);
  
  const addSystemLog = useCallback((msg, type = 'info') => {
    setLogSystem(p => [...p.slice(-50), { ts: ts(), msg, type }]);
  }, []);

  // ─────────────────────────────────────────────────────────────────────────
  // MIC
  // ─────────────────────────────────────────────────────────────────────────
  const loadMics = async () => {
    try {
      addSystemLog('Richiedo permesso microfono...');
      await navigator.mediaDevices.getUserMedia({ audio: true });
      const devices = await navigator.mediaDevices.enumerateDevices();
      const audioInputs = devices.filter(d => d.kind === 'audioinput');
      setMics(audioInputs);
      
      const real = audioInputs.find(m => 
        !m.label.toLowerCase().includes('blackhole') &&
        !m.label.toLowerCase().includes('virtual')
      );
      if (real) {
        setMicId(real.deviceId);
        addSystemLog(`Microfono: ${real.label}`, 'success');
      } else if (audioInputs.length) {
        setMicId(audioInputs[0].deviceId);
      }
    } catch (e) {
      addSystemLog(`Errore mic: ${e.message}`, 'error');
    }
  };

  const toggleMonitor = async () => {
    if (monitoring) {
      if (animRef.current) cancelAnimationFrame(animRef.current);
      if (streamRef.current) streamRef.current.getTracks().forEach(t => t.stop());
      if (ctxRef.current) ctxRef.current.close();
      setMonitoring(false);
      setLivello(0);
      return;
    }
    
    try {
      const stream = await navigator.mediaDevices.getUserMedia({
        audio: micId ? { deviceId: { exact: micId } } : true
      });
      streamRef.current = stream;
      const ctx = new AudioContext();
      ctxRef.current = ctx;
      const src = ctx.createMediaStreamSource(stream);
      const analyser = ctx.createAnalyser();
      analyser.fftSize = 256;
      src.connect(analyser);
      
      const data = new Uint8Array(analyser.frequencyBinCount);
      let okCount = 0; // Conta frame con audio OK
      
      const update = () => {
        analyser.getByteFrequencyData(data);
        const avg = data.reduce((a, b) => a + b) / data.length;
        const lev = Math.min(100, avg * 1.5);
        setLivello(lev);
        
        // Se livello OK per 60 frame (~1 sec), conferma e ferma
        if (lev > 25) {
          okCount++;
          if (okCount > 60) {
            setMicOk(true);
            addSystemLog('Microfono OK!', 'success');
            // Auto-stop dopo conferma
            setTimeout(() => {
              if (animRef.current) cancelAnimationFrame(animRef.current);
              if (streamRef.current) streamRef.current.getTracks().forEach(t => t.stop());
              if (ctxRef.current) ctxRef.current.close();
              setMonitoring(false);
              setLivello(0);
            }, 500);
            return;
          }
        } else {
          okCount = Math.max(0, okCount - 2); // Resetta se silenzio
        }
        
        animRef.current = requestAnimationFrame(update);
      };
      update();
      setMonitoring(true);
    } catch (e) {
      addSystemLog(`Errore monitor: ${e.message}`, 'error');
    }
  };

  // ─────────────────────────────────────────────────────────────────────────
  // TEST CONN
  // ─────────────────────────────────────────────────────────────────────────
  const testConn = async () => {
    addSystemLog('Test connessione servizi...');
    const r = { stt: null, chat: null, tts11: null, ttsOAI: null };
    
    try {
      const res = await fetch(`${API_BASE_URL}/api/voice/transcribe`, { 
        method: 'POST', headers: { 'x-api-key': API_KEY } 
      });
      r.stt = res.status === 400 || res.ok;
    } catch { r.stt = false; }
    
    try {
      const res = await fetch(`${API_BASE_URL}/api/semantic/chat`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json', 'x-api-key': API_KEY },
        body: JSON.stringify({ userInput: 'test', userId: 1 })
      });
      r.chat = res.ok;
    } catch { r.chat = false; }
    
    try {
      const res = await fetch(`${API_BASE_URL}/api/voice/tts-multivoce`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json', 'x-api-key': API_KEY },
        body: JSON.stringify({ text: 'Test', forcedVoice: 'bella' })
      });
      const d = await res.json();
      r.tts11 = d.success === true;
    } catch { r.tts11 = false; }
    
    try {
      const res = await fetch(`${API_BASE_URL}/api/voice/tts`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json', 'x-api-key': API_KEY },
        body: JSON.stringify({ text: 'Test', voice: 'nova' })
      });
      r.ttsOAI = res.ok;
    } catch { r.ttsOAI = false; }
    
    setConn(r);
    addSystemLog(`Servizi pronti`, 'success');
  };

  // ─────────────────────────────────────────────────────────────────────────
  // RECORDING
  // ─────────────────────────────────────────────────────────────────────────
  const startRec = async () => {
    if (monitoring) toggleMonitor();
    setErrore('');
    setUtente('');
    setRisposta('');
    setTempi({});
    setDataFlow(null);
    setLogRag([]);
    setLogSemantic([]);
    
    try {
      addSystemLog('Avvio registrazione...');
      const stream = await navigator.mediaDevices.getUserMedia({
        audio: micId ? { deviceId: { exact: micId }, echoCancellation: true, noiseSuppression: true } : { echoCancellation: true }
      });
      
      streamRef.current = stream;
      recorderRef.current = new MediaRecorder(stream);
      chunksRef.current = [];
      
      recorderRef.current.ondataavailable = e => {
        if (e.data.size > 0) chunksRef.current.push(e.data);
      };
      
      recorderRef.current.onstop = async () => {
        const blob = new Blob(chunksRef.current, { type: 'audio/webm' });
        addSystemLog(`Audio: ${(blob.size/1024).toFixed(1)} KB`);
        await processAudio(blob);
      };
      
      recorderRef.current.start();
      setStato('recording');
      addSystemLog('In ascolto...', 'success');
    } catch (e) {
      addSystemLog(`Errore: ${e.message}`, 'error');
      setErrore(e.message);
      setStato('idle');
    }
  };

  const stopRec = () => {
    if (recorderRef.current && stato === 'recording') {
      addSystemLog('Stop registrazione');
      recorderRef.current.stop();
      streamRef.current?.getTracks().forEach(t => t.stop());
    }
  };

  // ─────────────────────────────────────────────────────────────────────────
  // PROCESS AUDIO
  // ─────────────────────────────────────────────────────────────────────────
  const processAudio = async (blob) => {
    setStato('thinking');
    const t0 = Date.now();
    
    try {
      addSystemLog('Trascrizione...');
      const t1 = Date.now();
      const fd = new FormData();
      fd.append('audio', blob, 'rec.webm');
      
      const sttRes = await fetch(`${API_BASE_URL}/api/voice/transcribe`, {
        method: 'POST',
        headers: { 'x-api-key': API_KEY },
        body: fd
      });
      
      if (!sttRes.ok) throw new Error('Trascrizione fallita');
      const sttData = await sttRes.json();
      const text = sttData.text;
      const sttTime = Date.now() - t1;
      
      addSystemLog(`Trascritto: "${text}"`, 'success');
      setTempi(p => ({ ...p, stt: sttTime }));
      setUtente(text);
      
      if (!text?.trim()) throw new Error('Non ho capito');
      
      await genResponse(text, t0);
    } catch (e) {
      addSystemLog(`Errore: ${e.message}`, 'error');
      setErrore(e.message);
      setStato('idle');
    }
  };

  const processTxt = async (txt) => {
    setErrore('');
    setUtente(txt);
    setRisposta('');
    setTempi({});
    setDataFlow(null);
    setLogRag([]);
    setLogSemantic([]);
    addSystemLog(`Domanda: "${txt}"`);
    await genResponse(txt, Date.now());
  };

  // Invia testo da input
  const handleSendText = () => {
    if (inputText.trim() && stato === 'idle') {
      processTxt(inputText.trim());
      setInputText('');
    }
  };

  const handleKeyPress = (e) => {
    if (e.key === 'Enter' && !e.shiftKey) {
      e.preventDefault();
      handleSendText();
    }
  };

  // ─────────────────────────────────────────────────────────────────────────
  // GENERA RISPOSTA
  // ─────────────────────────────────────────────────────────────────────────
  const genResponse = async (txt, t0) => {
    setStato('thinking');
    
    const flow = {
      input: txt,
      steps: [],
    };
    
    try {
      // SEMANTIC
      addSemanticLog('INIZIO ELABORAZIONE');
      addSemanticLog(`Input: "${txt}"`);
      addSemanticLog(`Endpoint: /api/semantic/chat`);
      
      const t2 = Date.now();
      const chatRes = await fetch(`${API_BASE_URL}/api/semantic/chat`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json', 'x-api-key': API_KEY },
        body: JSON.stringify({ userInput: txt, userId: 1 })
      });
      
      if (!chatRes.ok) throw new Error('Errore Semantic Chat');
      const chatData = await chatRes.json();
      const aiText = chatData.response || 'Ciao!';
      const chatTime = Date.now() - t2;
      
      addSemanticLog(`Risposta HTTP: OK`, 'success');
      addSemanticLog(`Tempo: ${chatTime}ms`);
      addSemanticLog(`Modello: ${chatData.model || 'gpt-4o-mini'}`);
      
      if (chatData.classification) {
        addSemanticLog(`Categoria: ${chatData.classification.category || 'N/A'}`);
        if (chatData.classification.confidence) {
          addSemanticLog(`Confidenza: ${(chatData.classification.confidence * 100).toFixed(1)}%`);
        }
      }
      
      if (chatData.usage) {
        addSemanticLog(`Token prompt: ${chatData.usage.prompt_tokens}`);
        addSemanticLog(`Token risposta: ${chatData.usage.completion_tokens}`);
        addSemanticLog(`Token totali: ${chatData.usage.total_tokens}`);
      }
      
      addSemanticLog(`Output: "${aiText.substring(0, 80)}..."`, 'success');
      addSemanticLog('FINE ELABORAZIONE', 'success');
      
      // RAG
      addRagLog('ANALISI RICORDI');
      addRagLog(`Fonte: ${chatData.source || 'database'}`);
      
      const memories = chatData.memories || [];
      addRagLog(`Ricordi trovati: ${memories.length}`, memories.length > 0 ? 'success' : 'warning');
      
      if (memories.length > 0) {
        memories.forEach((m, i) => {
          const content = typeof m === 'string' ? m : (m.content || m.text || JSON.stringify(m));
          addRagLog(`[${i+1}] ${content.substring(0, 100)}`);
          if (m.score) addRagLog(`    Similarita: ${(m.score * 100).toFixed(1)}%`);
        });
      } else {
        addRagLog('Nessun ricordo specifico, uso base', 'warning');
      }
      
      addRagLog('FLUSSO DATI:');
      addRagLog('1. Input -> /api/semantic/chat');
      addRagLog('2. Ricerca in SQLite (memories)');
      addRagLog('3. Embedding vettoriale');
      addRagLog('4. Contesto a GPT-4');
      addRagLog('5. Risposta personalizzata');
      addRagLog('FINE ANALISI', 'success');
      
      flow.totalLatency = chatTime;
      setDataFlow(flow);
      
      setTempi(p => ({ ...p, chat: chatTime }));
      setRisposta(aiText);

      // TTS
      setStato('speaking');
      addSystemLog(`TTS: ${servizio}`);
      
      const t3 = Date.now();
      let ok = false;
      
      if (servizio === 'elevenlabs') ok = await playElevenlabs(aiText);
      else if (servizio === 'openai') ok = await playOpenAI(aiText);
      
      if (!ok && servizio !== 'browser') {
        addSystemLog('Fallback browser', 'warning');
      }
      if (!ok || servizio === 'browser') {
        await speakWithBrowser(aiText);
      }
      
      const ttsTime = Date.now() - t3;
      const totale = Date.now() - t0;
      setTempi(p => ({ ...p, tts: ttsTime, totale }));
      addSystemLog(`Completato: ${totale}ms`, 'success');
      
    } catch (e) {
      addSystemLog(`Errore: ${e.message}`, 'error');
      addSemanticLog(`ERRORE: ${e.message}`, 'error');
      setErrore(e.message);
      setStato('idle');
    }
  };

  // TTS
  const playElevenlabs = async (text) => {
    try {
      const res = await fetch(`${API_BASE_URL}/api/voice/tts-multivoce`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json', 'x-api-key': API_KEY },
        body: JSON.stringify({ text, forcedVoice: voce11 })
      });
      const d = await res.json();
      if (d.success && d.audio) {
        await playBase64(d.audio);
        return true;
      }
      return false;
    } catch { return false; }
  };

  const playOpenAI = async (text) => {
    try {
      const res = await fetch(`${API_BASE_URL}/api/voice/tts`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json', 'x-api-key': API_KEY },
        body: JSON.stringify({ text, voice: voceOAI })
      });
      if (!res.ok) return false;
      const d = await res.json();
      if (d.audio) {
        await playBase64(d.audio);
        return true;
      }
      return false;
    } catch { return false; }
  };

  const speakWithBrowser = (text) => new Promise(resolve => {
    const u = new SpeechSynthesisUtterance(text);
    u.lang = 'it-IT';
    u.rate = 0.9;
    u.onend = () => { setStato('idle'); resolve(); };
    u.onerror = () => { setStato('idle'); resolve(); };
    speechSynthesis.speak(u);
  });

  const playBase64 = (b64) => new Promise(resolve => {
    const audio = new Audio(`data:audio/mpeg;base64,${b64}`);
    audio.onended = () => { setStato('idle'); resolve(); };
    audio.onerror = () => { setStato('idle'); resolve(); };
    audio.play().catch(() => { setStato('idle'); resolve(); });
  });

  const onClick = () => {
    if (stato === 'idle') startRec();
    else if (stato === 'recording') stopRec();
  };

  useEffect(() => {
    addSystemLog('DonatApp v9 avviato');
    loadMics();
    testConn();
  }, []);

  // ─────────────────────────────────────────────────────────────────────────
  // RENDER
  // ─────────────────────────────────────────────────────────────────────────
  return (
    <div style={{
      minHeight: '100vh',
      background: C.white,
      fontFamily: '-apple-system, BlinkMacSystemFont, "SF Pro Text", system-ui, sans-serif',
      padding: isMobile ? 16 : 32,
      color: C.black,
    }}>
      <div style={{ maxWidth: 700, margin: '0 auto' }}>
        
        {/* HEADER */}
        <header style={{ textAlign: 'center', marginBottom: 24 }}>
          <h1 style={{ fontSize: isMobile ? 28 : 36, fontWeight: 700, color: C.black, margin: 0 }}>
            DonatApp
          </h1>
          <p style={{ fontSize: 16, color: C.gray500, margin: '8px 0 0' }}>
            Assistente vocale
          </p>
        </header>

        {/* STATUS */}
        <div style={{ display: 'flex', justifyContent: 'center', gap: 8, marginBottom: 24, flexWrap: 'wrap' }}>
          {[
            { k: 'stt', n: 'Whisper' },
            { k: 'chat', n: 'Semantic' },
            { k: 'tts11', n: 'ElevenLabs' },
            { k: 'ttsOAI', n: 'OpenAI' },
          ].map(s => (
            <span key={s.k} style={{
              padding: '6px 12px',
              borderRadius: 4,
              fontSize: 13,
              fontWeight: 500,
              background: conn[s.k] === null ? C.gray100 : conn[s.k] ? C.gray100 : C.gray100,
              color: conn[s.k] === null ? C.gray400 : conn[s.k] ? C.black : C.gray400,
              border: `1px solid ${conn[s.k] ? C.black : C.gray200}`,
            }}>
              {s.n}
            </span>
          ))}
        </div>

        {/* CUORE */}
        <div style={{ display: 'flex', justifyContent: 'center', padding: '16px 0 32px' }}>
          <div style={{ position: 'relative' }}>
            <div style={{
              position: 'absolute',
              top: '50%', left: '50%',
              transform: 'translate(-50%, -50%)',
              width: 180, height: 180,
              borderRadius: '50%',
              border: `2px solid ${C.heartLight}`,
              opacity: stato !== 'idle' ? 0.6 : 0.2,
              animation: stato !== 'idle' ? 'ripple 2s infinite' : 'none',
            }} />
            
            <button
              onClick={onClick}
              disabled={stato === 'thinking' || stato === 'speaking'}
              style={{
                width: 130,
                height: 130,
                borderRadius: '50%',
                background: C.heart,
                border: 'none',
                cursor: stato === 'thinking' || stato === 'speaking' ? 'default' : 'pointer',
                boxShadow: `0 8px 32px rgba(220, 38, 38, 0.3)`,
                display: 'flex',
                alignItems: 'center',
                justifyContent: 'center',
                transform: stato === 'recording' ? 'scale(1.08)' : 'scale(1)',
                transition: 'transform 0.2s',
              }}
            >
              <svg viewBox="0 0 24 24" width={55} height={55}
                style={{ animation: stato !== 'idle' ? 'pulse 1s infinite' : 'none' }}>
                <path
                  fill={C.white}
                  d="M12 21.35l-1.45-1.32C5.4 15.36 2 12.28 2 8.5 2 5.42 4.42 3 7.5 3c1.74 0 3.41.81 4.5 2.09C13.09 3.81 14.76 3 16.5 3 19.58 3 22 5.42 22 8.5c0 3.78-3.4 6.86-8.55 11.54L12 21.35z"
                />
              </svg>
            </button>
          </div>
        </div>

        {/* STATO */}
        <p style={{ fontSize: 18, fontWeight: 500, color: C.black, textAlign: 'center', margin: '0 0 24px' }}>
          {stato === 'idle' && 'Tocca il cuore per parlare'}
          {stato === 'recording' && 'Ti sto ascoltando...'}
          {stato === 'thinking' && 'Elaborazione...'}
          {stato === 'speaking' && 'Sto rispondendo...'}
        </p>

        {/* INPUT TESTUALE */}
        <div style={styles.card}>
          <p style={styles.cardTitle}>Scrivi qui</p>
          <div style={{ display: 'flex', gap: 10 }}>
            <input
              type="text"
              value={inputText}
              onChange={(e) => setInputText(e.target.value)}
              onKeyPress={handleKeyPress}
              placeholder="Scrivi la tua domanda..."
              disabled={stato !== 'idle'}
              style={{
                flex: 1,
                padding: '14px 16px',
                fontSize: 16,
                borderRadius: 8,
                border: `2px solid ${C.gray200}`,
                background: C.white,
                color: C.black,
                outline: 'none',
                opacity: stato === 'idle' ? 1 : 0.5,
              }}
            />
            <button
              onClick={handleSendText}
              disabled={stato !== 'idle' || !inputText.trim()}
              style={{
                padding: '14px 24px',
                fontSize: 16,
                fontWeight: 600,
                borderRadius: 8,
                border: 'none',
                background: inputText.trim() && stato === 'idle' ? C.black : C.gray200,
                color: inputText.trim() && stato === 'idle' ? C.white : C.gray400,
                cursor: inputText.trim() && stato === 'idle' ? 'pointer' : 'default',
              }}
            >
              Invia
            </button>
          </div>
        </div>

        {/* MESSAGGI - Subito dopo input */}
        {utente && (
          <div style={{ ...styles.card, background: C.gray50, borderLeft: `4px solid ${C.black}` }}>
            <p style={{ fontSize: 12, fontWeight: 600, color: C.gray500, margin: '0 0 6px', textTransform: 'uppercase' }}>Tu</p>
            <p style={{ fontSize: 17, color: C.black, margin: 0, lineHeight: 1.5 }}>{utente}</p>
          </div>
        )}
        
        {risposta && (
          <div style={{ ...styles.card, background: C.gray50, borderLeft: `4px solid ${C.gray400}` }}>
            <p style={{ fontSize: 12, fontWeight: 600, color: C.gray500, margin: '0 0 6px', textTransform: 'uppercase' }}>Assistente</p>
            <p style={{ fontSize: 17, color: C.black, margin: 0, lineHeight: 1.5 }}>{risposta}</p>
          </div>
        )}
        
        {errore && (
          <div style={{ ...styles.card, background: '#FFF5F5', borderLeft: `4px solid ${C.heart}` }}>
            <p style={{ fontSize: 12, fontWeight: 600, color: C.heart, margin: '0 0 6px', textTransform: 'uppercase' }}>Errore</p>
            <p style={{ fontSize: 15, color: C.black, margin: 0 }}>{errore}</p>
          </div>
        )}

        {/* TEMPI */}
        {tempi.totale && (
          <div style={{ display: 'flex', justifyContent: 'center', flexWrap: 'wrap', gap: 8, margin: '16px 0' }}>
            {tempi.stt && <span style={styles.tag}>STT {tempi.stt}ms</span>}
            {tempi.chat && <span style={styles.tag}>AI {tempi.chat}ms</span>}
            {tempi.tts && <span style={styles.tag}>TTS {tempi.tts}ms</span>}
            <span style={{ ...styles.tag, background: C.black, color: C.white }}>Totale {tempi.totale}ms</span>
          </div>
        )}

        {/* MICROFONO - Prima di Voce */}
        <div style={styles.card}>
          <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
            <p style={{ ...styles.cardTitle, margin: 0 }}>Microfono</p>
            {micOk ? (
              <span style={{
                padding: '6px 14px', borderRadius: 4,
                fontSize: 13, fontWeight: 600,
                background: C.gray100, color: C.black,
                border: `1px solid ${C.black}`,
              }}>
                OK
              </span>
            ) : (
              <button onClick={toggleMonitor} style={{
                padding: '6px 14px', borderRadius: 4, 
                border: `1px solid ${C.black}`,
                fontSize: 13, fontWeight: 500,
                background: monitoring ? C.black : C.white, 
                color: monitoring ? C.white : C.black, 
                cursor: 'pointer',
              }}>
                {monitoring ? 'Stop' : 'Test'}
              </button>
            )}
          </div>
          
          {/* Mostra select e barra SOLO durante test o se non ancora OK */}
          {(monitoring || !micOk) && (
            <>
              <select value={micId} onChange={e => setMicId(e.target.value)} style={{
                width: '100%', padding: 12, borderRadius: 4, 
                border: `1px solid ${C.gray200}`,
                fontSize: 15, marginTop: 12, marginBottom: 10, background: C.white,
              }}>
                {mics.map(m => <option key={m.deviceId} value={m.deviceId}>{m.label || `Mic ${m.deviceId.slice(0,8)}`}</option>)}
              </select>
              {monitoring && (
                <>
                  <div style={{ height: 4, background: C.gray200, borderRadius: 2, overflow: 'hidden' }}>
                    <div style={{ height: '100%', width: `${livello}%`, background: C.black, transition: 'width 0.1s' }} />
                  </div>
                  <p style={{ 
                    fontSize: 13, 
                    margin: '10px 0 0', 
                    color: livello > 20 ? C.black : C.gray400,
                    fontWeight: livello > 20 ? 600 : 400,
                  }}>
                    {livello > 40 ? 'Ottimo! Audio chiaro' : livello > 20 ? 'OK - Audio sufficiente' : 'Parla piu forte o avvicinati'}
                  </p>
                </>
              )}
            </>
          )}
        </div>

        {/* VOCE - Dopo Microfono */}
        <div style={styles.card}>
          <p style={styles.cardTitle}>Voce</p>
          <div style={{ display: 'flex', gap: 8, marginBottom: 12 }}>
            {['elevenlabs', 'openai', 'browser'].map(s => (
              <button
                key={s}
                onClick={() => setServizio(s)}
                style={{
                  flex: 1,
                  padding: '10px 8px',
                  borderRadius: 4,
                  border: `2px solid ${servizio === s ? C.black : C.gray200}`,
                  background: servizio === s ? C.black : C.white,
                  color: servizio === s ? C.white : C.gray600,
                  fontSize: 14,
                  fontWeight: 500,
                  cursor: 'pointer',
                }}
              >
                {s === 'elevenlabs' ? 'ElevenLabs' : s === 'openai' ? 'OpenAI' : 'Browser'}
              </button>
            ))}
          </div>
          
          {servizio !== 'browser' && (
            <div style={{ display: 'flex', flexWrap: 'wrap', gap: 6 }}>
              {(servizio === 'elevenlabs' ? VOCI_11 : VOCI_OAI).map(v => (
                <button
                  key={v.id}
                  onClick={() => servizio === 'elevenlabs' ? setVoce11(v.id) : setVoceOAI(v.id)}
                  style={{
                    padding: '8px 14px',
                    borderRadius: 4,
                    border: `1px solid ${(servizio === 'elevenlabs' ? voce11 : voceOAI) === v.id ? C.black : C.gray200}`,
                    fontSize: 13,
                    fontWeight: 500,
                    background: (servizio === 'elevenlabs' ? voce11 : voceOAI) === v.id ? C.gray100 : C.white,
                    color: C.black,
                    cursor: 'pointer',
                  }}
                >
                  {v.nome}
                </button>
              ))}
            </div>
          )}
        </div>

        {/* FRASI RAPIDE */}
        <div style={styles.card}>
          <p style={styles.cardTitle}>Domande rapide</p>
          <div style={{ display: 'flex', flexWrap: 'wrap', gap: 6 }}>
            {FRASI.map((f, i) => (
              <button key={i} onClick={() => processTxt(f)} disabled={stato !== 'idle'} style={{
                padding: '8px 14px', borderRadius: 4, 
                border: `1px solid ${C.gray200}`,
                background: C.white, fontSize: 13, color: C.black,
                cursor: stato === 'idle' ? 'pointer' : 'default', 
                opacity: stato === 'idle' ? 1 : 0.5,
              }}>
                {f}
              </button>
            ))}
          </div>
        </div>

        {/* ═══════════════════════════════════════════════════════════════════ */}
        {/* 3 LOG SEPARATI - MONOCROMATICI */}
        {/* ═══════════════════════════════════════════════════════════════════ */}
        
        {/* LOG RAG */}
        <div style={styles.logBox}>
          <button onClick={() => setShowRagLog(!showRagLog)} style={styles.logBtn}>
            <span style={{ fontWeight: 600 }}>RAG LOG</span>
            <span style={{ color: C.gray400 }}>Ricordi e memorie ({logRag.length})</span>
            <span>{showRagLog ? '−' : '+'}</span>
          </button>
          {showRagLog && (
            <div style={styles.logContent}>
              {logRag.length === 0 ? (
                <p style={styles.logEmpty}>I log RAG appariranno qui. Vedrai quali ricordi vengono recuperati.</p>
              ) : (
                logRag.map((l, i) => (
                  <div key={i} style={{
                    ...styles.logLine,
                    color: l.type === 'error' ? C.heart : l.type === 'success' ? C.black : l.type === 'warning' ? C.gray600 : C.gray500,
                    fontWeight: l.type === 'success' ? 600 : 400,
                  }}>
                    <span style={{ color: C.gray400, marginRight: 8 }}>{l.ts}</span>
                    {l.msg}
                  </div>
                ))
              )}
            </div>
          )}
        </div>

        {/* LOG SEMANTIC */}
        <div style={styles.logBox}>
          <button onClick={() => setShowSemanticLog(!showSemanticLog)} style={styles.logBtn}>
            <span style={{ fontWeight: 600 }}>SemanticAi Log</span>
            <span style={{ color: C.gray400 }}>Elaborazione ({logSemantic.length})</span>
            <span>{showSemanticLog ? '−' : '+'}</span>
          </button>
          {showSemanticLog && (
            <div style={styles.logContent}>
              {logSemantic.length === 0 ? (
                <p style={styles.logEmpty}>I log Semantic appariranno qui. Vedrai come l'AI elabora la richiesta.</p>
              ) : (
                logSemantic.map((l, i) => (
                  <div key={i} style={{
                    ...styles.logLine,
                    color: l.type === 'error' ? C.heart : l.type === 'success' ? C.black : l.type === 'warning' ? C.gray600 : C.gray500,
                    fontWeight: l.type === 'success' ? 600 : 400,
                  }}>
                    <span style={{ color: C.gray400, marginRight: 8 }}>{l.ts}</span>
                    {l.msg}
                  </div>
                ))
              )}
            </div>
          )}
        </div>

        {/* LOG SISTEMA */}
        <div style={styles.logBox}>
          <button onClick={() => setShowSystemLog(!showSystemLog)} style={styles.logBtn}>
            <span style={{ fontWeight: 600 }}>SYSTEM LOG</span>
            <span style={{ color: C.gray400 }}>Log tecnico ({logSystem.length})</span>
            <span>{showSystemLog ? '−' : '+'}</span>
          </button>
          {showSystemLog && (
            <div style={styles.logContent}>
              {logSystem.map((l, i) => (
                <div key={i} style={{
                  ...styles.logLine,
                  color: l.type === 'error' ? C.heart : l.type === 'success' ? C.black : l.type === 'warning' ? C.gray600 : C.gray500,
                  fontWeight: l.type === 'success' ? 600 : 400,
                }}>
                  <span style={{ color: C.gray400, marginRight: 8 }}>{l.ts}</span>
                  {l.msg}
                </div>
              ))}
            </div>
          )}
        </div>

        {/* FOOTER */}
        <p style={{ textAlign: 'center', fontSize: 13, color: C.gray400, marginTop: 32 }}>
          DonatApp v9
        </p>
      </div>

      <style>{`
        @keyframes pulse { 0%, 100% { transform: scale(1); } 50% { transform: scale(1.05); } }
        @keyframes ripple { 0% { transform: translate(-50%, -50%) scale(0.9); opacity: 0.6; } 100% { transform: translate(-50%, -50%) scale(1.3); opacity: 0; } }
      `}</style>
    </div>
  );
};

const styles = {
  card: {
    background: '#FFFFFF',
    borderRadius: 8,
    padding: 18,
    marginBottom: 14,
    border: '1px solid #EEEEEE',
  },
  cardTitle: {
    fontSize: 12,
    fontWeight: 600,
    color: '#555555',
    margin: '0 0 10px',
    textTransform: 'uppercase',
    letterSpacing: '0.5px',
  },
  tag: {
    padding: '6px 12px',
    borderRadius: 4,
    fontSize: 12,
    fontWeight: 500,
    background: '#F5F5F5',
    color: '#111111',
    border: '1px solid #DDDDDD',
  },
  logBox: {
    borderRadius: 8,
    overflow: 'hidden',
    marginBottom: 12,
    border: '1px solid #DDDDDD',
    background: '#FFFFFF',
  },
  logBtn: {
    width: '100%',
    padding: '16px 20px',
    border: 'none',
    background: '#F5F5F5',
    color: '#111111',
    fontSize: 14,
    cursor: 'pointer',
    display: 'flex',
    justifyContent: 'space-between',
    alignItems: 'center',
    gap: 12,
  },
  logContent: {
    background: '#FAFAFA',
    maxHeight: 300,
    overflowY: 'auto',
    borderTop: '1px solid #EEEEEE',
  },
  logLine: {
    padding: '10px 16px',
    fontSize: 14,
    fontFamily: 'SF Mono, Monaco, Consolas, monospace',
    borderBottom: '1px solid #EEEEEE',
    lineHeight: 1.5,
  },
  logEmpty: {
    color: '#999999',
    fontSize: 14,
    margin: 0,
    padding: 16,
    fontStyle: 'italic',
  },
};

export default VoiceECuore;
